import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { Card, CardContent } from '../components/ui/card';
import GenericHero from '../components/GenericHero';
import TeamTree from '../components/TeamTree';

const TeamPage = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchTeam = async () => {
      setLoading(true);
      setError('');
      const { data, error: supabaseError } = await supabase
        .from('team')
        .select(`
          id,
          name,
          image,
          images,
          bio,
          display_order,
          is_active,
          team_roles (
            name,
            is_board_role
          )
        `)
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (supabaseError) {
        setError('Unable to load team at the moment. Please try again later.');
        setTeamMembers([]);
      } else {
        const normalized = (data || []).map((member) => ({
          id: member.id,
          name: member.name,
          role: member.team_roles?.name || 'Team',
          isBoard: Boolean(member.team_roles?.is_board_role),
          image: member.image || (Array.isArray(member.images) ? member.images[0] : ''),
          bio: member.bio || '',
          display_order: member.display_order ?? 0,
        }));
        setTeamMembers(normalized);
      }
      setLoading(false);
    };

    fetchTeam();
  }, []);

  const boardMembers = useMemo(
    () => teamMembers.filter((m) => m.isBoard),
    [teamMembers]
  );
  const nonBoardMembers = useMemo(
    () => teamMembers.filter((m) => !m.isBoard),
    [teamMembers]
  );

  const treeData = useMemo(
    () => ({
      title: 'NyiNimu',
      children: [
        {
          title: 'Board',
          children: boardMembers.map((person) => ({ person })),
        },
        {
          title: 'Team',
          children: nonBoardMembers.map((person) => ({ person })),
        },
      ],
    }),
    [boardMembers, nonBoardMembers]
  );

  return (
    <div className="relative">
      {/* Hero Section covers navbar */}
      <div className="absolute inset-0 h-[40vh] w-full -z-10">
        <img
          src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80"
          alt="Our Team"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black bg-opacity-50"></div>
      </div>
      <div className="pt-0">
        <GenericHero
          title="Our Team"
          description="Meet the dedicated professionals working to make your homeownership dreams a reality."
          backgroundVideo="https://ik.imagekit.io/hqhiltiie/Nyinimu/hero%20bg.mp4?updatedAt=1763033048497"
        />
      </div>

      {/* Tree Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-light title-font text-stone-800 mb-4">
              Our Team Structure
            </h2>
            <p className="text-stone-600 text-lg max-w-3xl mx-auto">
              Explore our organization from the Board to the core team. Expand nodes to view members.
            </p>
          </div>

          <TeamTree data={treeData} />
        </div>
      </section>

      {/* Card grid section */}
      <section className="pb-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div>
            <h3 className="text-3xl font-semibold text-stone-800 mb-6">Our Team</h3>
            {loading && (
              <p className="text-stone-600">Loading team...</p>
            )}
            {error && (
              <p className="text-red-600">{error}</p>
            )}
            <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {!loading && !error && nonBoardMembers.map((member) => (
                <Card key={member.name} className="border-stone-200 shadow-sm hover:shadow-lg transition-shadow">
                  <div className="h-80 w-full overflow-hidden rounded-t-xl">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="h-full w-full object-cover transition-transform duration-500 hover:scale-105"
                    />
                  </div>
                  <CardContent className="pt-6 space-y-3">
                    <div>
                      <p className="text-xs font-semibold tracking-[0.3em] text-[#81be00] uppercase mb-1">Team</p>
                      <h4 className="text-xl font-semibold text-stone-900">{member.name}</h4>
                      <p className="text-sm font-medium text-stone-500 uppercase tracking-wide">{member.role}</p>
                    </div>
                    <p className="text-sm text-stone-600 leading-relaxed">{member.bio}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {!loading && !error && boardMembers.length > 0 && (
              <div className="mt-14">
                <h3 className="text-3xl font-semibold text-stone-800 mb-6">Board Members</h3>
                <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
                  {boardMembers.map((member) => (
                    <Card key={member.name} className="border-stone-200 shadow-sm hover:shadow-lg transition-shadow">
                      <div className="h-80 w-full overflow-hidden rounded-t-xl">
                        <img
                          src={member.image}
                          alt={member.name}
                          className="h-full w-full object-cover transition-transform duration-500 hover:scale-105"
                        />
                      </div>
                      <CardContent className="pt-6 space-y-3">
                        <div>
                          <p className="text-xs font-semibold tracking-[0.3em] text-[#81be00] uppercase mb-1">Board</p>
                          <h4 className="text-xl font-semibold text-stone-900">{member.name}</h4>
                          <p className="text-sm font-medium text-stone-500 uppercase tracking-wide">{member.role}</p>
                        </div>
                        <p className="text-sm text-stone-600 leading-relaxed">{member.bio}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default TeamPage;
